import styled, {css} from "styled-components";
import {Typography} from "@g4p/react-styleguide-library";

export const palette = {
    // Brand
    red600: "#e30c25",
    red700: "#cc0b15",
    red800: "#e2001a", // R1 Brand Color
    red900: "#b01200",

    // Default
    dark: "#262626", // K8
    white: "#fff",
    transparent: "rgba(255, 255, 255, 0)",
    grey: "#808080",
    black: "#000000",
    darkGrey: "#6F6F6F",

    // Neutral
    neutral900: "#909090",
    neutral800: "#333333",
    neutral700: "#666666", // K7
    neutral500: "#999999",
    neutral400: "#cccccc",
    neutral300: "#e5e5e5",
    neutral200: "#eeeeee",
    neutral100: "#f5f5f5",
    boxCardBackground: "#f5f5f5",

    // Interactive
    blue: "#0000ff",
    blue900: "#074861", // Bonds
    blue800: "#007a91", // T1 Primary
    blue700: "#0e8298",
    blue600: "#00afd0",
    blue500: "#00acc2",
    blue100: "#bfebf3",
    blue50: "#f0fcfe",
    secondaryT2: "#006375", // T2 Secondary
    secondaryA2: "#244858",

    // Feedback
    success1000: "#00a197",
    success900: "#006531",
    success800: "#148545",
    success600: "#2FA358",
    success50: "#e7f6ec",
    successG5: "#3D7D43", // G5 Success/Performance
    warning1000: "#EC6608",
    warning800: "#f26b08",
    warning600: "#fe8d08",
    warning500: "#fbb800",
    warning50: "#fff8e1",
    error900: "#c61811",
    error800: "#eb0000",
    error700: "#e2001a",
    error400: "#ea5c4d",
    error100: "#f6b6Bd",
    error50: "#ffe7e6",
    errorR5: "#CF3524", // R5 Error
    skeletonBgColor: "#cccccc",

    //PieChart
    chart1: "#00a3e0",
    chart2: "#5bc199",
    chart3: "#094a62",
    chart4: "#885bc1",

    // Support
    background: "#f6f9f9",
    neutral700a20: "rgba(102, 102, 102, 0.2)",
    neutral700a70: "rgba(102, 102, 102, 0.7)",
    neutral300a80: "rgba(229, 229, 229, 0.8)",

    404040 : "#404040"
} as const;

export type PaletteColor = typeof palette[keyof typeof palette];

export const containerRowMaxWidth = 1440;

export const fontFamily = "UniCredit";

export const fontWeight = {
    normal: 400,
    medium: 500,
    bold: 700,
} as const;

export const borderRadius = {
    "x-small": "2px",
    small: "4px",
    medium: "8px",
    large: "16px",
    "x-large": "30px",
} as const;

function makeBorder(color: keyof typeof palette): string {
    return `1px solid ${palette[color]}`;
}
export const border = {
    transparent: `1px solid transparent`,
    white: makeBorder("white"),
    dark: makeBorder("dark"),
    neutral800: makeBorder("neutral800"),
    neutral700: makeBorder("neutral700"),
    neutral500: makeBorder("neutral500"),
    neutral400: makeBorder("neutral400"),
    neutral300: makeBorder("neutral300"),
    neutral200: makeBorder("neutral200"),
    blue800: makeBorder("blue800"),
    blue600: makeBorder("blue600"),
    blue100: makeBorder("blue100"),
    error700: makeBorder("error700"),
    error800: makeBorder("error800"),
    error900: makeBorder("error900"),
    warning600: makeBorder("warning600"),
    warning800: makeBorder("warning800"),
    success800: makeBorder("success800"),
};

/**
 *  @example alpha(palette.blue800, 80) // equivalent to rgba(38, 38, 38, 0.8)
 */
function alpha(color: PaletteColor, alpha: number) {
    const alphaHex = (Math.round((alpha / 100) * 255) + 0x10000)
        .toString(16)
        .substr(-2)
        .toUpperCase();
    return `${color}${alphaHex}`;
}

function makeElevation(
    shadow1: string,
    alpha1: number
): (color: PaletteColor) => string;
function makeElevation(
    shadow1: string,
    alpha1: number,
    shadow2: string,
    alpha2: number
): (color: PaletteColor) => string;
function makeElevation(
    shadow1: string,
    alpha1: number,
    shadow2?: string,
    alpha2?: number
): (color: PaletteColor) => string {
    return color => {
        if (shadow2 !== undefined && alpha2 !== undefined) {
            return `${shadow1} ${alpha(color, alpha1)}, ${shadow2} ${alpha(
                color,
                alpha2
            )}`;
        } else {
            return `${shadow1} ${alpha(color, alpha1)}`;
        }
    };
}

export const elevation = {
    xs: makeElevation("0px 1px 3px 0px", 5),
    s: makeElevation("0px 2px 10px 0px", 6, "0px 1px 6px 0px", 4),
    m: makeElevation("0px 6px 10px -1px", 6, "0px 2px 6px -1px", 6),
    l: makeElevation("0px 10px 15px -3px", 12, "0px 4px 6px -2px", 5),
    // NOTE: same elevation but on the top of the elements
    up_xs: makeElevation("0px -1px 3px 0px", 5),
    up_s: makeElevation("0px -2px 10px 0px", 6, "0px -1px 6px 0px", 4),
    up_m: makeElevation("0px -6px 10px -1px", 6, "0px -2px 6px -1px", 6),
    up_l: makeElevation("0px -10px 15px -3px", 12, "0px -4px 6px -2px", 5),
};

export const focus = {
    neutral700: elevation.l(palette.neutral700),
    blue800: elevation.l(palette.blue800),
    error900: elevation.l(palette.error900),
    warning800: elevation.l(palette.warning800),
    // NOTE(gabro): this is an approximation of focus.blue800.
    // We can't use box-shadow for SVG elements and the drop-shadow filter does
    // not allow controlling the spread
    blue800SvgFilter: `drop-shadow(0px 10px 15px ${alpha(
        palette.blue800,
        12
    )}) drop-shadow(0px 4px 6px ${alpha(palette.blue800, 5)})`,
    blue50: elevation.m(palette.blue800),
    success50: elevation.m(palette.success900),
    error50: elevation.m(palette.error800),
    warning50: elevation.m(palette.warning800),
};



export const spaceUnit = 4;

type HeadingProps = {
    $size?: string;
    $weight?: string;
    $color?: PaletteColor;
    $textAlign?: string;
};

export const HeadingStyled = styled(Typography.Text)<HeadingProps>`
  font-family: ${fontFamily};
  ${props =>
    props.$size === "large" &&
    css`
      font-size: 40px;
      line-height: 1;
    `};
  ${props =>
    props.$size === "medium" &&
    css`
      font-size: 35px;
      line-height: 1;
    `};
  ${props =>
    props.$size === "small" &&
    css`
      font-size: 28px;
      line-height: 100%;
    `};
  ${props =>
    props.$size === "x-small" &&
    css`
      font-size: 18px;
      line-height: 22px;
    `};
  ${props =>
    props.$weight === "regular" &&
    css`
      font-weight: ${fontWeight.normal};
    `};
  ${props =>
    props.$weight === "medium" &&
    css`
      font-weight: ${fontWeight.medium};
    `};
  ${props =>
    props.$weight === "bold" &&
    css`
      font-weight: ${fontWeight.bold};
    `};
  ${props =>
    props.$textAlign &&
    css`
      text-align: ${props.$textAlign};
    `};
  ${props =>
    props.$color &&
    css`
      color: ${props.$color};
    `};
  ${props =>
    !props.$color &&
    css`
      color: ${palette.dark};
    `};
`;